import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * PowerSamoray: removing items is instant and can be repeated right away.
 * - The trash button removes the row on screen at once and never locks the cart, so the next trash
 *   button can be tapped immediately (tap three in a row and all three go).
 * - Requests go to the server one after another in a queue and identify the line by its key, not
 *   by its position, so several removals in a row always hit the right items.
 * - Removed rows stay in the page (hidden) until the cart is re-rendered once, after the last
 *   request; that keeps the positions used by the buttons correct in the meantime.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #quantityTimers = new Map();
  #optimisticTotal = null;

  #handleQuantityChange = (event) => {
    if (!this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    const row = this.refs.cartItemRows[line - 1];
    if (!row || row.classList.contains('removing') || row.classList.contains('awaiting-removal')) return;
    this.#adjustCount(line, quantity);
    this.#adjustTotal(line, quantity);
    // Keep checkout waiting through the debounce window as well as the network request.
    window.psCartSync?.track(new Promise((resolve) => setTimeout(resolve, 350)));
    const key = row.dataset.key || String(line);
    clearTimeout(this.#quantityTimers.get(key));
    this.#quantityTimers.set(key, setTimeout(() => {
      this.#quantityTimers.delete(key);
      this.#onQuantityChange({ detail: { quantity, cartLine: line, cartKey: key } });
    }, 300));
  };

  #adjustCount(line, quantity) {
    const row = this.refs.cartItemRows[line - 1];
    const input = row?.querySelector('input[data-cart-line]');
    if (!input || !Number.isFinite(quantity)) return;
    const previous = Number(row.dataset.psOptimisticQuantity ?? input.defaultValue);
    if (!Number.isFinite(previous)) return;
    row.dataset.psOptimisticQuantity = String(quantity);
    if (quantity !== previous) {
      document.dispatchEvent(new CustomEvent('ps:cart-count-adjust', { detail: { delta: quantity - previous } }));
    }
  }

  /**
   * Keep the subtotal readable while Shopify confirms a quantity change.
   * Record the rendered line prices once, then apply absolute target quantities so
   * rapid taps and several pending removals cannot compound the same change.
   */
  #adjustTotal(line, quantity) {
    const total = this.refs.cartTotal;
    if (!total || !Number.isFinite(quantity)) return;

    if (!this.#optimisticTotal) {
      const template = total.getAttribute('value') || total.textContent || '';
      const amount = template.match(/\d[\d,]*(?:\.\d{1,2})?/);
      if (!amount) return;

      const lines = new Map();
      this.refs.cartItemRows.forEach((row, index) => {
        const originalQuantity = Number(row.querySelector('input[data-cart-line]')?.defaultValue);
        const priceText = row.querySelector('text-component[value]')?.getAttribute('value') || '';
        const price = priceText.match(/\d[\d,]*(?:\.\d{1,2})?/);
        if (!price || !Number.isFinite(originalQuantity) || originalQuantity <= 0) return;
        lines.set(row.dataset.key || String(index + 1), {
          originalQuantity,
          unitCents: Math.round(Number(price[0].replace(/,/g, '')) * 100 / originalQuantity),
          quantity: originalQuantity,
        });
      });

      this.#optimisticTotal = {
        template,
        originalAmount: amount[0],
        baseCents: Math.round(Number(amount[0].replace(/,/g, '')) * 100),
        lines,
      };
    }

    const row = this.refs.cartItemRows[line - 1];
    const item = this.#optimisticTotal.lines.get(row?.dataset.key || String(line));
    if (!item) return;
    item.quantity = quantity;

    const delta = Array.from(this.#optimisticTotal.lines.values()).reduce(
      (sum, entry) => sum + (entry.quantity - entry.originalQuantity) * entry.unitCents,
      0
    );
    const formatted = (Math.max(0, this.#optimisticTotal.baseCents + delta) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    total.textContent = this.#optimisticTotal.template.replace(this.#optimisticTotal.originalAmount, formatted);
    total.removeAttribute('aria-busy');
  }

  /** Server requests run one after another. */
  #queue = Promise.resolve();
  /** Requests queued or running. The cart is re-rendered when this goes back to 0. */
  #pending = 0;
  /** Latest successful response (the one used for the final re-render). */
  #lastSections = null;
  #needsFullRender = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartKey } = event.detail;
    const row = this.refs.cartItemRows.find((item) => item.dataset.key === cartKey);
    if (!row || row.classList.contains('removing') || row.classList.contains('awaiting-removal')) return;
    const line = this.refs.cartItemRows.indexOf(row) + 1;

    if (quantity === 0) {
      return this.onLineItemRemove(line, { optimisticApplied: true, key: cartKey });
    }

    this.updateQuantity({
      line,
      key: cartKey,
      quantity,
      action: 'change',
      optimisticApplied: true,
    });

    const textComponent = /** @type {TextComponent | undefined} */ (row.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line, options = {}) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    // Already on its way out (double tap): nothing to do.
    if (cartItemRowToRemove?.classList.contains('removing') || cartItemRowToRemove?.classList.contains('awaiting-removal')) return;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      key: options.key,
      optimisticApplied: options.optimisticApplied === true,
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    /* Keep the final line visible until Shopify confirms the empty cart. Otherwise the
       drawer shows no products while its old item count and total are still on screen. */
    const otherVisibleRows = this.refs.cartItemRows.some((row) =>
      !rowsToRemove.includes(row) && !row.classList.contains('removing') && row.style.display !== 'none'
    );
    if (!otherVisibleRows) {
      rowsToRemove.forEach((row) => row.classList.add('awaiting-removal'));
      return;
    }

    rowsToRemove.forEach((row) => {
      /* Hide instead of deleting: the rows keep their positions (the buttons refer to them by
         position) until the cart is re-rendered after the last request. */
      const hide = () => {
        // An older animation must not hide a surviving row after the section morph reuses it.
        if (row.classList.contains('removing')) row.style.setProperty('display', 'none', 'important');
      };

      if (prefersReducedMotion()) {
        row.classList.add('removing');
        return hide();
      }

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, hide);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;
    if (!config.optimisticApplied) {
      this.#adjustCount(line, quantity);
      this.#adjustTotal(line, quantity);
    }
    const row = this.refs.cartItemRows[line - 1];
    /* Identify the line by its key: positions change on the server as soon as a line is removed. */
    const key = config.key || row?.dataset.key;

    this.#pending += 1;

    this.#queue = this.#queue
      .then(() => this.#sendChange(key ? { id: key, quantity } : { line, quantity }, line))
      .catch((error) => {
        console.error(error);
        this.#needsFullRender = true;
      })
      .finally(() => {
        this.#pending -= 1;
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        if (this.#pending === 0) this.#renderLatest();
      });

    /* Check out waits for this change too (see ps-predictive-quick-add.js). */
    window.psCartSync?.track(this.#queue);
  }

  /**
   * Sends one change to the server.
   * @param {{ id?: string, line?: number, quantity: number }} change
   * @param {number} line - Position used for error messages.
   */
  async #sendChange(change, line) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...change,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
    const parsedResponseText = JSON.parse(await response.text());

    if (parsedResponseText.errors) {
      this.#needsFullRender = true;
      this.#handleCartError(line, parsedResponseText);
      return;
    }

    this.#lastSections = parsedResponseText.sections;

    const newSectionHTML = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    // Keeps the cart count in the header up to date after every single change.
    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );
  }

  /** Re-renders the cart once, from the latest server response, when no request is left. */
  #renderLatest() {
    resetShimmer(this);

    if (this.#needsFullRender || !this.#lastSections?.[this.sectionId]) {
      this.#needsFullRender = false;
      this.#lastSections = null;
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => this.#restoreRenderedRows());
      return;
    }

    const html = this.#lastSections[this.sectionId];
    this.#lastSections = null;
    morphSection(this.sectionId, html);
    this.#restoreRenderedRows();
  }

  /** A reused row may retain display:none from the deleted row during morphing. */
  #restoreRenderedRows() {
    this.#optimisticTotal = null;
    const current = Array.from(document.querySelectorAll('cart-items-component')).find(
      (component) => component.dataset.sectionId === this.sectionId
    );
    current?.querySelectorAll('.cart-items__table-row').forEach((row) => {
      row.classList.remove('removing', 'awaiting-removal');
      row.style.removeProperty('display');
      row.style.removeProperty('--row-height');
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) return;
    if (!(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
